import { Link } from 'react-router-dom';
import { FileText, Shield, Lock, ArrowRight } from 'lucide-react';
import SEO from '@/components/SEO';
import '@/styles/article.css';
import '@/styles/landing.css';

const legalDocs = [
  {
    icon: FileText,
    title: 'Privacy Policy',
    desc: 'How we collect, use, and protect your personal data.',
    to: '/privacy/',
  },
  {
    icon: FileText,
    title: 'Terms of Service',
    desc: 'The rules and conditions governing your use of UXAuditPro.',
    to: '/terms/',
  },
  {
    icon: Lock,
    title: 'Security',
    desc: 'How we keep your data and uploads secure.',
    to: '/security/',
  },
];

export default function LegalPage() {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Legal"
        description="UXAuditPro legal documents — Privacy Policy, Terms of Service, and Security practices. Everything you need to know about how we operate."
        canonical="/legal"
      />
      {/* THE MEASURE STAYS AT max-w-3xl. These are article pages; article.css
          binds --article-measure to 68ch and the module was verified conformant
          on 2026-08-19. The brand shell goes around the column, never through
          it. The grid wash sits behind the document header only. */}
      <div className="max-w-3xl mx-auto px-6 py-16" style={{ position: 'relative' }}>
        {/* Header */}
        <div className="mb-12" style={{ position: 'relative' }}>
          <div className="uxa-hero-grid" aria-hidden="true" /* inset was '-64px -40px 0' and produced 16px of horizontal
                   overflow at 390 and 768 — the column is px-6 (24px), so a
                   -40px bleed has nowhere to go. Measured on the built page:
                   scrollWidth 406 against a 390 client. A decorative wash is
                   never a reason for the page to scroll sideways. */
                style={{ inset: '-64px 0 0' }} />
          <div style={{ position: 'relative' }}>
          <span className="text-sm font-bold uppercase tracking-widest" style={{ color: 'var(--accent-text)' }}>UXAuditPro</span>
          <h1 className="uxa-h1 mt-3" style={{ color: 'var(--ink-1)', textWrap: 'pretty' }}>
            Legal
          </h1>
          <p className="mt-4 text-base" style={{ color: 'var(--ink-2)' }}>
            All the legal documents governing your use of UXAuditPro.
          </p>
          </div>
        </div>

        {/* Document cards */}
        <div className="space-y-4 mb-12">
          {legalDocs.map(({ icon: Icon, title, desc, to }) => (
            <Link
              key={to}
              to={to}
              className="uxa-feature-card flex items-center justify-between group"
              style={{ background: 'var(--surface-white)', padding: 'var(--space-6)' }}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent-tint)' }}>
                  <Icon className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
                </div>
                <div>
                  <h3 className="uxa-h3" style={{ color: 'var(--ink-1)' }}>{title}</h3>
                  <p className="uxa-dense mt-0.5" style={{ color: 'var(--ink-2)' }}>{desc}</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 opacity-40 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--ink-1)' }} />
            </Link>
          ))}
        </div>

        {/* Company info */}
        <div className="uxa-feature-card" style={{ background: 'var(--surface-white)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--accent)' }}>
              <Shield className="w-4 h-4 text-white" aria-hidden="true" />
            </div>
            <h2 className="uxa-h2" style={{ color: 'var(--ink-1)' }}>About the Company</h2>
          </div>
          <div className="uxa-article">
            <p>
              UXAuditPro is an independent AI-powered UX audit platform. We build tools to help product teams ship better user experiences.
            </p>
            <p>
              For legal enquiries, contact us at{' '}
              <a href="mailto:legal@uxauditpro.com">legal@uxauditpro.com</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
